/**
 * Colour-vision-deficiency simulation.
 *
 * Protanopia/protanomaly (missing or weak long-wavelength cones), deuteranopia/
 * deuteranomaly (medium) and tritanopia/tritanomaly (short) use the matrices of
 *
 *   Machado, G. M., Oliveira, M. M., & Fernandes, L. A. F. (2009). A physiologically-based
 *   model for simulation of color vision deficiency. IEEE Transactions on Visualization
 *   and Computer Graphics, 15(6), 1291–1298.
 *
 * as published by the authors at
 * https://www.inf.ufrgs.br/~oliveira/pubs_files/CVD_Simulation/CVD_Simulation.html —
 * one 3 × 3 matrix per severity from 0.0 (normal) to 1.0 (dichromacy) in steps of 0.1.
 * The matrices act on LINEAR RGB: each sRGB channel is decoded to linear light, multiplied,
 * clamped to 0–1 and encoded again. A matrix applied to gamma-encoded values would shift
 * mid-tones wrongly. Every row of every matrix sums to 1, so greys (and white) are kept.
 *
 * Intermediate severities interpolate linearly between the two nearest tabulated
 * matrices, which is how the authors describe it.
 *
 * Achromatopsia (rod monochromacy) is NOT in the Machado model. It is drawn as relative
 * luminance — the same Y = 0.2126 R + 0.7152 G + 0.0722 B used for WCAG contrast — in
 * linear light, shown as a neutral grey. That is an approximation of the lightness a
 * person without cone function sees; it does not represent the reduced acuity and light
 * sensitivity that usually come with the condition.
 */

import {
  linearToSrgb,
  srgbToLinear,
} from "@/lib/tool-kit/color-charts/color-space";

export type CvdType = "protan" | "deutan" | "tritan" | "achroma";

export type Matrix = readonly [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
];

/** Machado et al. (2009), severity 0.0 to 1.0 in steps of 0.1, row-major. */
const PROTAN: readonly Matrix[] = [
  [1, 0, 0, 0, 1, 0, 0, 0, 1],
  [
    0.856_167, 0.182_038, -0.038_205, 0.029_342, 0.955_115, 0.015_544,
    -0.002_88, -0.001_563, 1.004_443,
  ],
  [
    0.734_766, 0.334_872, -0.069_637, 0.051_84, 0.919_198, 0.028_963,
    -0.004_928, -0.004_209, 1.009_137,
  ],
  [
    0.630_323, 0.465_641, -0.095_964, 0.069_181, 0.890_046, 0.040_773,
    -0.006_308, -0.007_724, 1.014_032,
  ],
  [
    0.539_009, 0.579_343, -0.118_352, 0.082_546, 0.866_121, 0.051_332,
    -0.007_136, -0.011_959, 1.019_095,
  ],
  [
    0.458_064, 0.679_578, -0.137_642, 0.092_785, 0.846_313, 0.060_902,
    -0.007_494, -0.016_807, 1.024_301,
  ],
  [
    0.385_45, 0.769_005, -0.154_455, 0.100_526, 0.829_802, 0.069_673,
    -0.007_442, -0.022_19, 1.029_632,
  ],
  [
    0.319_627, 0.849_633, -0.169_261, 0.106_241, 0.815_969, 0.077_79,
    -0.007_025, -0.028_051, 1.035_076,
  ],
  [
    0.259_411, 0.923_008, -0.182_42, 0.110_296, 0.804_34, 0.085_364, -0.006_276,
    -0.034_346, 1.040_622,
  ],
  [
    0.203_876, 0.990_338, -0.194_214, 0.112_975, 0.794_542, 0.092_483,
    -0.005_222, -0.041_043, 1.046_265,
  ],
  [
    0.152_286, 1.052_583, -0.204_868, 0.114_503, 0.786_281, 0.099_216,
    -0.003_882, -0.048_116, 1.051_998,
  ],
];

const DEUTAN: readonly Matrix[] = [
  [1, 0, 0, 0, 1, 0, 0, 0, 1],
  [
    0.866_435, 0.177_704, -0.044_139, 0.049_567, 0.939_063, 0.011_37,
    -0.003_453, 0.007_233, 0.996_22,
  ],
  [
    0.760_729, 0.319_078, -0.079_807, 0.090_568, 0.889_315, 0.020_117,
    -0.006_027, 0.013_325, 0.992_702,
  ],
  [
    0.675_425, 0.433_85, -0.109_275, 0.125_303, 0.847_755, 0.026_942, -0.007_95,
    0.018_572, 0.989_378,
  ],
  [
    0.605_511, 0.528_56, -0.134_071, 0.155_318, 0.812_366, 0.032_316,
    -0.009_376, 0.023_176, 0.9862,
  ],
  [
    0.547_494, 0.607_765, -0.155_259, 0.181_692, 0.781_742, 0.036_566,
    -0.010_41, 0.027_275, 0.983_136,
  ],
  [
    0.498_864, 0.674_741, -0.173_604, 0.205_199, 0.754_872, 0.039_929,
    -0.011_131, 0.030_969, 0.980_162,
  ],
  [
    0.457_771, 0.731_899, -0.189_67, 0.226_409, 0.731_012, 0.042_579,
    -0.011_595, 0.034_333, 0.977_261,
  ],
  [
    0.422_823, 0.781_057, -0.203_881, 0.245_752, 0.709_602, 0.044_646,
    -0.011_843, 0.037_423, 0.974_421,
  ],
  [
    0.392_952, 0.823_61, -0.216_562, 0.263_559, 0.690_21, 0.046_232, -0.011_91,
    0.040_281, 0.971_63,
  ],
  [
    0.367_322, 0.860_646, -0.227_968, 0.280_085, 0.672_501, 0.047_413,
    -0.011_82, 0.042_94, 0.968_881,
  ],
];

const TRITAN: readonly Matrix[] = [
  [1, 0, 0, 0, 1, 0, 0, 0, 1],
  [
    0.926_67, 0.092_514, -0.019_184, 0.021_191, 0.964_503, 0.014_306, 0.008_437,
    0.054_813, 0.936_75,
  ],
  [
    0.895_72, 0.133_33, -0.029_05, 0.029_997, 0.9454, 0.024_603, 0.013_027,
    0.104_707, 0.882_266,
  ],
  [
    0.905_871, 0.127_791, -0.033_662, 0.026_856, 0.941_251, 0.031_893, 0.013_41,
    0.148_296, 0.838_294,
  ],
  [
    0.948_035, 0.089_49, -0.037_526, 0.014_364, 0.946_792, 0.038_844, 0.010_853,
    0.193_991, 0.795_156,
  ],
  [
    1.017_277, 0.027_029, -0.044_306, -0.006_113, 0.958_479, 0.047_634,
    0.006_379, 0.248_708, 0.744_913,
  ],
  [
    1.104_996, -0.046_633, -0.058_363, -0.032_137, 0.971_635, 0.060_503,
    0.001_336, 0.317_922, 0.680_742,
  ],
  [
    1.193_214, -0.109_812, -0.083_402, -0.058_496, 0.979_41, 0.079_086,
    -0.002_346, 0.403_492, 0.598_854,
  ],
  [
    1.257_728, -0.139_648, -0.118_081, -0.078_003, 0.975_409, 0.102_594,
    -0.003_316, 0.501_214, 0.502_102,
  ],
  [
    1.278_864, -0.125_333, -0.153_531, -0.084_748, 0.957_674, 0.127_074,
    -0.000_989, 0.601_151, 0.399_838,
  ],
  [
    1.255_528, -0.076_749, -0.178_779, -0.078_411, 0.930_809, 0.147_602,
    0.004_733, 0.691_367, 0.3039,
  ],
];

const TABLES: Record<Exclude<CvdType, "achroma">, readonly Matrix[]> = {
  protan: PROTAN,
  deutan: DEUTAN,
  tritan: TRITAN,
};

/** The matrix for a severity 0–1, interpolated between the 0.1 steps of the table. */
export function cvdMatrix(
  type: Exclude<CvdType, "achroma">,
  severity: number
): Matrix {
  const table = TABLES[type];
  const position = Math.min(1, Math.max(0, severity)) * 10;
  const low = Math.min(9, Math.floor(position + 1e-9));
  const t = position - low;
  const a = table[low];
  const b = table[low + 1];
  return a.map(
    (value, index) => value + (b[index] - value) * t
  ) as unknown as Matrix;
}

/* ------------------------------------------------------------------- pixels */

const LINEAR_OF_BYTE: Float64Array = (() => {
  const lut = new Float64Array(256);
  for (let i = 0; i < 256; i++) {
    lut[i] = srgbToLinear(i / 255);
  }
  return lut;
})();

const ENCODE_STEPS = 4096;

/** linear 0–1 → sRGB byte, through a 4096-entry table (error under half a code value). */
const BYTE_OF_LINEAR: Uint8Array = (() => {
  const lut = new Uint8Array(ENCODE_STEPS + 1);
  for (let i = 0; i <= ENCODE_STEPS; i++) {
    lut[i] = Math.round(linearToSrgb(i / ENCODE_STEPS) * 255);
  }
  return lut;
})();

function encode(linear: number): number {
  const clamped = Math.min(1, Math.max(0, linear));
  return BYTE_OF_LINEAR[Math.round(clamped * ENCODE_STEPS)];
}

/** One sRGB colour (bytes) through the simulation. */
export function simulateColor(
  rgb: readonly [number, number, number],
  type: CvdType,
  severity = 1
): [number, number, number] {
  const r = LINEAR_OF_BYTE[rgb[0]];
  const g = LINEAR_OF_BYTE[rgb[1]];
  const b = LINEAR_OF_BYTE[rgb[2]];
  if (type === "achroma") {
    const y = encode(0.2126 * r + 0.7152 * g + 0.0722 * b);
    return [y, y, y];
  }
  const m = cvdMatrix(type, severity);
  return [
    encode(m[0] * r + m[1] * g + m[2] * b),
    encode(m[3] * r + m[4] * g + m[5] * b),
    encode(m[6] * r + m[7] * g + m[8] * b),
  ];
}

/**
 * Simulate a whole RGBA image (the layout of `ImageData.data`). Returns a new array;
 * alpha is copied untouched.
 */
export function simulatePixels(
  data: Uint8ClampedArray | Uint8Array,
  type: CvdType,
  severity = 1
): Uint8ClampedArray {
  const out = new Uint8ClampedArray(data.length);
  const m = type === "achroma" ? null : cvdMatrix(type, severity);
  for (let i = 0; i < data.length; i += 4) {
    const r = LINEAR_OF_BYTE[data[i]];
    const g = LINEAR_OF_BYTE[data[i + 1]];
    const b = LINEAR_OF_BYTE[data[i + 2]];
    if (m) {
      out[i] = encode(m[0] * r + m[1] * g + m[2] * b);
      out[i + 1] = encode(m[3] * r + m[4] * g + m[5] * b);
      out[i + 2] = encode(m[6] * r + m[7] * g + m[8] * b);
    } else {
      const y = encode(0.2126 * r + 0.7152 * g + 0.0722 * b);
      out[i] = y;
      out[i + 1] = y;
      out[i + 2] = y;
    }
    out[i + 3] = data[i + 3];
  }
  return out;
}

/* -------------------------------------------------------------- sample image */

/**
 * A built-in test picture, as RGBA bytes: a full hue sweep (where the confusion lines
 * of each deficiency show as flat or repeating bands), the eight Okabe–Ito colours, and
 * the five-class red–yellow–green palette that the guidance on colour-blind-safe design
 * warns against.
 */
export function sampleImagePixels(
  width: number,
  height: number
): Uint8ClampedArray {
  const data = new Uint8ClampedArray(width * height * 4);
  const okabe = [
    [0, 114, 178],
    [230, 159, 0],
    [0, 158, 115],
    [213, 94, 0],
    [86, 180, 233],
    [204, 121, 167],
    [240, 228, 66],
    [0, 0, 0],
  ];
  const rdylgn = [
    [215, 25, 28],
    [253, 174, 97],
    [255, 255, 191],
    [166, 217, 106],
    [26, 150, 65],
  ];
  const band = Math.floor(height / 3);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let rgb: readonly number[];
      if (y < band) {
        rgb = hueColor((x / width) * 360);
      } else if (y < 2 * band) {
        rgb = okabe[Math.min(7, Math.floor((x / width) * 8))];
      } else {
        rgb = rdylgn[Math.min(4, Math.floor((x / width) * 5))];
      }
      const at = (y * width + x) * 4;
      data[at] = rgb[0];
      data[at + 1] = rgb[1];
      data[at + 2] = rgb[2];
      data[at + 3] = 255;
    }
  }
  return data;
}

function hueColor(hue: number): [number, number, number] {
  const k = (n: number) => (n + hue / 30) % 12;
  const f = (n: number) =>
    0.5 - 0.5 * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1));
  return [
    Math.round(f(0) * 255),
    Math.round(f(8) * 255),
    Math.round(f(4) * 255),
  ];
}

/** Scale `width × height` to fit `maxSide` on the longer side, never enlarging. */
export function fitWithin(
  width: number,
  height: number,
  maxSide: number
): { width: number; height: number } {
  const scale = Math.min(1, maxSide / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}
